import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const { getCartItemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const cartCount = getCartItemCount();

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/about', label: 'About' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-[#09090b] text-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 hover:opacity-90 transition-opacity"
            data-testid="logo-link"
          >
            <img 
              src="https://res.cloudinary.com/dlqzefke5/image/upload/v1769067688/b_transparent_e8jtkd.png" 
              alt="G.O.A.T. Athletic Logo" 
              className="h-10 md:h-12 w-auto"
            />
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight hidden sm:inline">
              G.O.A.T. ATHLETIC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-medium uppercase tracking-wider text-sm transition-colors ${
                  isActive(link.to) ? 'text-[#00ff9d]' : 'text-white hover:text-[#00ff9d]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Link 
              to="/cart" 
              className="relative p-2 hover:text-[#00ff9d] transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 bg-[#00ff9d] text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 hover:text-[#00ff9d] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#09090b] border-t border-zinc-800"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`font-medium uppercase tracking-wider text-sm transition-colors py-2 ${
                    isActive(link.to) ? 'text-[#00ff9d]' : 'text-white hover:text-[#00ff9d]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
